import { omit, includes } from "./utils";
import { parseDate } from "./dates";
import dayjs from 'dayjs'; // dayjs duration plugin is extended globally in app/javascript/packs/application.ts
export function afterNewRetentionDate() { return new Date() > retentionDate(); } // Calculates a start and end date for the Timeline component.
export function timelineDateRange(fromYear = null, toYear = null) { const today = new Date(); const currentYear = today.getFullYear(); const _afterNewRetentionDate = afterNewRetentionDate(); if (fromYear == null) {
    fromYear = currentYear - (_afterNewRetentionDate ? 4 : 5);
} if (toYear == null) {
    toYear = _afterNewRetentionDate ? currentYear : currentYear - 1;
} return [dayjs(Date.UTC(fromYear, 3, 6)), dayjs(new Date(toYear, 3, 5))]; } // Figures out which months on the Timeline component should be marked as filled.
export function timelineMonths(startDate, endDate, filledDateRanges, loaded) { const monthCount = Math.floor(dayjs.duration(endDate.diff(startDate)).as('months')); const result = []; for (let i = 0; i < monthCount + 1; i++) {
    const firstEntry = i === 0;
    const lastEntry = i === monthCount;
    let periodStart = startDate.add(i, 'month');
    let periodEnd = toLastDayOfMonth(periodStart);
    if (lastEntry) {
        periodEnd = periodStart;
        periodStart = toFirstDayOfMonth(periodStart);
    }
    else if (!firstEntry) {
        periodStart = toFirstDayOfMonth(periodStart);
    }
    result.push({ date: periodStart, filled: loaded && !inDateRanges(periodStart, periodEnd, filledDateRanges) });
} return result; }
export function dateRangesForTimelineGaps(timelineGaps, resourceName, onlyDisplayCurrentGaps = false, employments = []) {
    if (!timelineGaps) {
        return [];
    }
    let filteredGaps = timelineGaps.filter(g => g.resource_name === resourceName);
    if (onlyDisplayCurrentGaps) { // For CIS users, we only care about gaps that lead up to the present day
        const today = dayjs().startOf('day');
        filteredGaps = filteredGaps.filter(g => dayjs(g.ended_at).isSame(today) || dayjs(g.ended_at).isAfter(today));
    }
    return filteredGaps.map(g => {
        const startedAtArr = g.started_at.split('-'); // yyyy-MM-dd
        const endedAtArr = g.ended_at.split('-'); // yyyy-MM-dd
        return { related_resource_id: g.related_resource_id, preceding_resource_id: g.preceding_resource_id, succeeding_resource_id: g.succeeding_resource_id, display_data: g.display_data ? { ...g.display_data, actions: timelineGapActions(g, { employments }) } : g.display_data, started_at: dayjs(new Date(startedAtArr[0], startedAtArr[1] - 1, startedAtArr[2])), ended_at: dayjs(new Date(endedAtArr[0], endedAtArr[1] - 1, endedAtArr[2])) };
    });
} // Whether a yyyy-MM-dd date string refers to today - used to omit an "ended_at" query param
// for timeline gap actions when the gap is still open-ended (mirrors the API's Date#today?).
function isTodayDateString(dateString) { return dateString === dayjs().format('YYYY-MM-DD'); } // Builds a path with a query string, omitting any null/undefined params - mirrors Rails'
// polymorphic_path/url_for behaviour of dropping nil query params entirely.
function gapActionHref(path, params) { const query = new URLSearchParams(); Object.entries(params).forEach(([key, value]) => { if (value !== null && value !== undefined) {
    query.set(key, String(value));
} }); const queryString = query.toString(); return queryString ? `${path}?${queryString}` : path; }
function baseGapParams(gap) { return { started_at: gap.started_at, ended_at: isTodayDateString(gap.ended_at) ? null : gap.ended_at }; }
function addressGapActions(gap) { return [{ modifiers: ['primary', 'mini'], href: gapActionHref('/addresses/new', baseGapParams(gap)), imgAlt: 'Add Address', imgName: 'plusWhite', srText: 'Add Address' }]; }
function employmentGapActions(gap, employments = []) {
    const baseParams = baseGapParams(gap);
    const result = [{ componentName: 'AnchorButton', classes: ['button-group__item'], modifiers: ['primary', 'mini'], href: gapActionHref('/employments/new', { ...baseParams, self_employed: false }), children: 'Add an employer', asRouterLink: true }]; // We should only show the unemployed option when the user has already added an employment:
    if (employments && employments.length) {
        result.push({ componentName: 'AnchorButton', classes: ['button-group__item'], modifiers: ['primary', 'mini'], href: gapActionHref('/employment_gaps/new', baseParams), children: 'Unemployed', asRouterLink: true });
    }
    return result;
}
function locationGapActions(gap) { const params = { ...baseGapParams(gap) }; if (gap.related_resource_id) {
    params.person_employment_id = gap.related_resource_id;
} return [{ componentName: 'AnchorButton', classes: ['button-group__item'], modifiers: ['primary', 'mini'], href: gapActionHref('/work_locations/new', params), children: 'Add a workplace', asRouterLink: true }, { componentName: 'AnchorButton', classes: ['button-group__item'], modifiers: ['primary', 'mini'], href: gapActionHref('/work_location_gaps/new', params), children: "I wasn't travelling to work", asRouterLink: true }]; } // Builds the "add missing X" action button(s) for a raw timeline gap - equivalent of the
// display-only action-building previously done server-side in Api::Person::TimelineGap.
export function timelineGapActions(gap, { employments = [] } = {}) { switch (gap.resource_name) {
    case 'Person::Address': return addressGapActions(gap);
    case 'Person::Employment': return employmentGapActions(gap, employments);
    case 'Person::Location': return locationGapActions(gap);
    default: return [];
} }
export function generateDisplayListActions(resources, timelineGaps, dataToOmit = [], skipRetentionPrompts = false, additionalRetentionPromptData = {}) {
    if (!resources) {
        return [];
    } // Join the resources and timeline gaps together, sort them by date:
    const ids = resources.map(res => res.id);
    const resourcesAndGaps = (timelineGaps || []).filter(gap => isDisplayableTimelineGap(gap, ids)).concat(resources).sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
    if (!skipRetentionPrompts) {
        attachRetentionPrompts(resourcesAndGaps, additionalRetentionPromptData);
    } // Return the display data for the resulting list (to be passed to React components):
    return resourcesAndGaps.map(obj => omit(obj.display_data, dataToOmit));
}
export function latestClaimableTaxYearStartDate() {
    const currentYear = new Date().getFullYear();
    const year = afterNewRetentionDate() ? currentYear - 1 : currentYear - 2;
    return new Date(year, 3, 6); // April 6 of latest claimable tax year
} // Private
function isDisplayableTimelineGap(gap, resourceIds) { const { succeeding_resource_id, preceding_resource_id } = gap; if (!gap.display_data) {
    return false;
} const hasNoResource = !succeeding_resource_id && !preceding_resource_id; const hasSucceedingResource = includes(resourceIds, succeeding_resource_id); const hasPrecedingResource = includes(resourceIds, preceding_resource_id); return hasNoResource || hasSucceedingResource || hasPrecedingResource; }
function attachRetentionPrompts(resourcesAndGaps, additionalRetentionPromptData) {
    for (let i = 0; i < resourcesAndGaps.length; i++) {
        const resource = resourcesAndGaps[i];
        if (needsRetentionPrompt(resource)) {
            attachRentionPrompt(resourcesAndGaps, i, resource, additionalRetentionPromptData);
            i++; // We know the next item is the retention prompt we just attached, so skip it.
        }
    }
}
function needsRetentionPrompt(resource) {
    const resourceValid = resource.valid !== false; // Deliberately checking this is NOT false. Other falsy values like undefined and null are ok.
    const resourceCreatedAt = parseDate(resource.created_at);
    const resourceConfirmedAt = parseDate(resource.last_confirmed_at);
    const _retentionDate = retentionDate();
    return resource.id && // Timeline gaps don't have IDs
        resourceValid && !resource.ended_at && (afterNewRetentionDate() && resourceCreatedAt && resourceCreatedAt < _retentionDate && !resourceConfirmedAt || afterNewRetentionDate() && resourceConfirmedAt && resourceConfirmedAt < _retentionDate);
}
function attachRentionPrompt(resourcesAndGaps, index, lastResource, additionalRetentionPromptData) {
    let timelineString, questionHeading, questionText = null;
    const resourceHeading = lastResource.display_data.heading;
    const resourceType = inferResourceType(lastResource);
    switch (resourceType) {
        case 'address':
            timelineString = `Do you still live at <strong>${resourceHeading}</strong>?`;
            questionHeading = 'Change of address';
            questionText = `When did you move out of ${resourceHeading}?`;
            break;
        case 'employment':
            const modEmployment = !!lastResource.mod_branch;
            timelineString = `Are you still working for<strong>${modEmployment ? ' the' : ''} ${resourceHeading}</strong>?`;
            questionHeading = 'End of employment';
            questionText = `When did you leave${modEmployment ? ' the' : ''} ${resourceHeading}?`;
            break;
        case 'employmentGap':
            timelineString = `Are you still <strong>unemployed</strong>?`;
            questionHeading = 'End of unemployment';
            questionText = `When did your unemployment end?`;
            break;
        case 'location':
            timelineString = `Are you still working at <strong>${resourceHeading}</strong>?`;
            questionHeading = 'Change of workplace';
            questionText = `When did you stop working at ${resourceHeading}?`;
            break;
        case 'locationGap':
            timelineString = `Are you still <strong>not travelling to work</strong>?`;
            questionHeading = 'End of non-travel period';
            questionText = `When did you start travelling to work again?`;
            break;
        default: // These are never intended to display, but provide a serviceable default if for some
            // reason this ends up being accidentally used for an unsupported resource:
            timelineString = `Are you still at <strong>${resourceHeading}</strong>?`;
            questionHeading = 'Change of end date';
            questionText = `When did you stop at ${resourceHeading}?`;
    }
    const displayData = { timelineString, questionHeading, questionText, componentName: 'TimelineEndedAtPrompt', resourceType: resourceType, resourceId: lastResource.id };
    if (resourceType === 'location') { // These additional attributes are included so the retntion prompt can validate the supplied date
        // falls within the date of the employment this location is related to.
        displayData.relatedResourceHeading = additionalRetentionPromptData.heading;
        displayData.minDate = parseDate(additionalRetentionPromptData.startedAt);
        displayData.maxDate = parseDate(additionalRetentionPromptData.endedAt, new Date());
    }
    resourcesAndGaps.splice(index, 0, { display_data: displayData });
}
function inferResourceType(resource) { switch (resource.class_name) {
    case 'Api::Person::Address':
        return 'address';
        break;
    case 'Api::Person::Employment':
        return 'employment';
        break;
    case 'Api::Person::EmploymentGap':
        return 'employmentGap';
        break;
    case 'Api::Person::Location':
        return 'location';
        break;
    case 'Api::Person::LocationGap':
        return 'locationGap';
        break;
} }
function inDateRanges(periodStart, periodEnd, timelineGapDateRanges) { return timelineGapDateRanges.some(({ started_at, ended_at }) => { return periodStart >= started_at && periodEnd <= ended_at; }); }
function toFirstDayOfMonth(date) { return date.date(1).hour(1).minute(0).second(0); }
function toLastDayOfMonth(date) { return date.add(1, 'month').date(1).hour(1).minute(0).second(0).subtract(1, 'day'); }
function retentionDate() { const day = window.myRIFT?.config?.retentionDay; const month = window.myRIFT?.config?.retentionMonth - 1; const year = new Date().getFullYear(); return new Date(year, month, day, 0, 0, 0, 0); }
